import { cloneDeep } from 'lodash'
import {
  MutationCondition,
  MutationDefinition,
  MutationRule,
  StepSchema
} from '@feedyou-ai/feedbot-schema'
import {
  Mutable,
  MutableProps,
  Mutated,
  MutationChanges,
  SelectedMutations
} from './types/mutation'
import { AnyObject, Dictionary } from './types/utility'
import { areGivenStringsEqual, isNotEmptyStringOrWhiteSpace } from './stringOperation'
import { STABLE_EMPTY_ARRAY } from './constants'

export function mutateStep<Step extends StepSchema>(
  step: Mutable<Step>,
  selectedMutations: SelectedMutations
): Mutated<Step> {
  const mutatedStep = mutate(
    cloneDeep(step),
    cloneDeep(step.mutations) ?? STABLE_EMPTY_ARRAY,
    selectedMutations
  )

  if (mutatedStep.carousel) {
    mutatedStep.carousel = mutatedStep.carousel.map((carouselItem: any) => {
      if (carouselItem.buttons) {
        carouselItem.buttons = carouselItem.buttons.map((button: any) =>
          mutate(cloneDeep(button), cloneDeep(button.mutations), selectedMutations)
        )
      }

      return mutate(cloneDeep(carouselItem), cloneDeep(carouselItem.mutations), selectedMutations)
    })
  }

  if (mutatedStep.answers) {
    mutatedStep.answers = mutatedStep.answers.map((answer: any) =>
      mutate(cloneDeep(answer), cloneDeep(answer.mutations), selectedMutations)
    )
  }

  return mutatedStep
}

export function mutate<O extends AnyObject>(
  object: Mutable<O>,
  mutations: MutationRule<O>[],
  selectedMutations: SelectedMutations
): Mutated<O> {
  const changes = mutationChanges<O>(mutations, selectedMutations)
  return { ...object, ...changes, changes }
}

export function mutationChanges<O extends AnyObject>(
  mutations: MutationRule<O>[],
  selectedMutations: SelectedMutations
): MutationChanges<O> {
  const changes: MutationChanges<O> = {}

  ;(mutations || []).filter(validateMutation(selectedMutations)).forEach(mutation => {
    if (!isSelectedMutation(mutation.test, selectedMutations)) {
      return
    }

    if (!mutation || !mutation.set) {
      return
    }

    mutation.set.forEach(set => {
      if (!set?.field) return
      ;(changes as any)[set.field] = set.value
    })
  })

  return changes
}

function isSelectedMutation(
  test: MutationCondition[] | undefined,
  selectedMutations: SelectedMutations
): boolean {
  if (!test) {
    return false
  }

  if (test.length !== Object.keys(selectedMutations).length) {
    return false
  }

  for (const item of test) {
    if (
      !item.field ||
      !selectedMutations[item.field] ||
      selectedMutations[item.field] !== item.value
    ) {
      return false
    }
  }

  return true
}

export function updateMutation<
  Obj extends Mutable<AnyObject>,
  Key extends MutableProps<Obj>,
  Val extends Obj[Key] | undefined
>(item: Obj, field: Key, value: Val, selectedMutations: SelectedMutations) {
  if (!item) {
    return item
  }

  const itemCopy = cloneDeep(item)
  const foundMutations = itemCopy.mutations?.filter(validateMutation(selectedMutations))

  if (
    foundMutations &&
    foundMutations.length > 0 &&
    isExistingMutation(itemCopy, field, selectedMutations)
  ) {
    const found: Dictionary<Key, boolean | MutationRule<Obj>> = {}

    itemCopy.mutations?.forEach(mutation => {
      if (!isSelectedMutation(mutation.test, selectedMutations)) {
        return
      }

      if (!mutation.set) {
        return
      }

      mutation.set.forEach(set => {
        if (!set || set.field !== field) {
          found[field] = !found[field] && { test: mutation.test, set: [{ field, value }] }
          return
        }

        set.value = value
        found[field] = true
      })
    })

    return insertMissingSet(itemCopy, found)
  }

  if (!foundMutations) {
    itemCopy.mutations = []
  }

  itemCopy?.mutations?.push({
    set: [{ field, value }],
    test: Object.entries(selectedMutations).map(([key, value]) => ({
      field: key,
      value: value
    }))
  })

  return itemCopy
}

function validateMutation<O extends Partial<Record<string, string>>>(
  selectedMutations: SelectedMutations
): (mutation: MutationRule<O>) => boolean {
  return mutation =>
    mutation?.test?.every(
      test => test && test.field && selectedMutations[test.field] === test.value
    ) || false
}

function insertMissingSet<Obj extends AnyObject, Key extends MutableProps<Obj>>(
  item: Obj,
  found: Dictionary<Key, boolean | MutationRule<Obj>>
): Mutable<AnyObject> {
  const itemCopy = cloneDeep(item)
  for (const field in found) {
    const mutation = found[field]
    if (typeof mutation === 'boolean') {
      continue
    }

    itemCopy?.mutations?.push(mutation)
  }

  return itemCopy
}

function isExistingMutation<Obj extends Mutable<AnyObject>, Key extends MutableProps<Obj>>(
  item: Obj,
  field: Key,
  selectedMutations: SelectedMutations
): boolean {
  return !!item?.mutations?.some(
    mutation =>
      isSelectedMutation(mutation.test, selectedMutations) &&
      mutation.set?.find(setItem => setItem.field === field)
  )
}

export function isMutationConditionPresentInBotMutations(
  botMutations: MutationDefinition[] = []
): (mutationCondition: MutationCondition) => boolean {
  return mutationCondition =>
    !!mutationCondition &&
    isNotEmptyMutationCondition(mutationCondition) &&
    botMutations.some(
      botMutation =>
        areGivenStringsEqual(botMutation.id, mutationCondition.field!) &&
        botMutation.options?.some((option: any) =>
          areGivenStringsEqual(option.id, mutationCondition.value!)
        )
    )
}

export function isNotEmptyMutationCondition(mutationCondition: MutationCondition): boolean {
  return (
    isNotEmptyStringOrWhiteSpace(mutationCondition.field) &&
    isNotEmptyStringOrWhiteSpace(mutationCondition.value)
  )
}
